// @ts-nocheck
import Button from '@rio-cloud/rio-uikit/Button';
import Table, { TableBody, TableColumn, TableHeader, TableHeaderColumn, TableRow } from '@rio-cloud/rio-uikit/Table';
import useLocalStorage from '@rio-cloud/rio-uikit/useLocalStorage';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
    downloadProductActivationsAction,
    pdcFetchMoreInspectionsAction,
    pdcSelectedAction,
    showPdcSidebarAction,
} from '@/actions/pdcActions';
import {
    DEFAULT_LOCALE,
    PDC_SEVERITY_HIGH,
    PDC_SEVERITY_HIGH_CLASS_NAME,
    PDC_SEVERITY_LOW_CLASS_NAME,
    PDC_SEVERITY_MEDIUM,
    PDC_SEVERITY_MEDIUM_CLASS_NAME,
} from '@/constants';
import { formatDateTime } from '@/lib/dateFormatter';
import getServiceLocation from '@/lib/getServiceLocation';
import { getInspections } from '@/selectors';

import AnalyticsStore from './AnalyticsStore';
import PDCOverviewTiles from './PDCOverviewTiles';
import PDFDownloadButtonComponent from './PDFDownloadButtonComponent';
import TableFilterBar from './TableFilterBar';
import { downloadFile, tableToCSV } from './utils/TableExportUtils';

const SortOrder = {
    ASC: 'asc',
    DESC: 'desc',
};

const NO_DATA_CHAR = '-';
const NO_DATA_TEXT = <span>{NO_DATA_CHAR}</span>;
const PDC_TABLE_COLUMNS_STORAGE_KEY = 'predeparturecheck.table.columns';

// Default columns in the same order and with the same visibility as the table before the migration.
// The UIKIT 3 table gives every column without a width the same share of the table (1fr), which cuts off the VIN,
// the headers and the trailer name. These px widths were measured in the browser: header label, icon and sort arrows,
// or the longest unbreakable cell value, plus the cell padding. Driver and vehicle names wrap like on main, so those two
// columns have no width and share the remaining space. A width saved by the user (resize) wins over these.
export const getInitialColumns = formatMessage => [
    {
        key: 'criticalIssueCount',
        width: 150,
        label: formatMessage({ id: 'intl-msg:pdc.table.header.criticalIssueCount' }),
        sortable: true,
        visible: true,
    },
    {
        key: 'issueCount',
        width: 105,
        label: formatMessage({ id: 'intl-msg:pdc.table.header.issueCount' }),
        sortable: true,
        visible: true,
    },
    {
        key: 'reportedBy',
        label: formatMessage({ id: 'intl-msg:pdc.table.header.reportedBy' }),
        icon: 'driver',
        sortable: true,
        visible: true,
    },
    {
        key: 'vehicleNameInfo',
        label: formatMessage({ id: 'intl-msg:pdc.table.header.vehicleNameInfo' }),
        icon: 'truck',
        sortable: true,
        visible: true,
    },
    {
        key: 'vin',
        width: 155,
        label: formatMessage({ id: 'intl-msg:pdc.table.header.vin' }),
        sortable: true,
        visible: true,
    },
    {
        key: 'trailerNameInfo',
        width: 135,
        label: formatMessage({ id: 'intl-msg:pdc.table.header.trailerNameInfo' }),
        icon: 'trailer',
        sortable: true,
        visible: true,
    },
    {
        key: 'modificationTimestamp',
        width: 150,
        label: formatMessage({ id: 'intl-msg:pdc.table.header.modificationTimestamp' }),
        sortable: true,
        visible: true,
    },
    {
        key: 'pdfLink',
        width: 125,
        label: formatMessage({ id: 'intl-msg:download' }),
        hideLabel: true,
        visible: true,
        resizeable: false,
        draggable: false,
        disabled: true,
    },
];

export const normalizeTableColumns = (storedColumns, formatMessage) => {
    const initialColumns = getInitialColumns(formatMessage);

    if (!Array.isArray(storedColumns) || storedColumns.length === 0) {
        return initialColumns;
    }

    const initialColumnsByKey = new Map(initialColumns.map(column => [column.key, column]));
    const normalizedColumns = storedColumns.flatMap(storedColumn => {
        const initialColumn = initialColumnsByKey.get(storedColumn.key);

        if (!initialColumn) {
            return [];
        }

        return [
            {
                ...initialColumn,
                visible: storedColumn.visible !== false,
                ...(storedColumn.width === undefined ? {} : { width: storedColumn.width }),
            },
        ];
    });

    return [
        ...normalizedColumns,
        ...initialColumns.filter(initialColumn => !normalizedColumns.some(column => column.key === initialColumn.key)),
    ];
};

export const getSeverityClass = row => {
    switch (row.severity) {
        case PDC_SEVERITY_HIGH:
            return PDC_SEVERITY_HIGH_CLASS_NAME;
        case PDC_SEVERITY_MEDIUM:
            return PDC_SEVERITY_MEDIUM_CLASS_NAME;
        default:
            return PDC_SEVERITY_LOW_CLASS_NAME;
    }
};

// eslint-disable-next-line react/display-name
export const getIssuesTranslationKey = issueCount => {
    // eslint-disable-next-line no-magic-numbers
    if (issueCount === 1) {
        return 'intl-msg:pdc.table.damage.count';
    }
    if (issueCount) {
        return 'intl-msg:pdc.table.damages.count';
    }

    return 'intl-msg:pdc.table.damages.none';
};

// eslint-disable-next-line react/display-name
export const getFormatIssuesColumn = (formatMessage, cell) => {
    const issueCount = Number(cell);
    const severityClass = issueCount > 0 ? 'text-color-warning' : '';

    return (
        <span
            className={`display-flex align-items-center gap-5 white-space-nowrap${severityClass ? ` ${severityClass}` : ''}`}
        >
            {severityClass ? <i className='uikit-icon icon-warning-sign' /> : ''}
            {formatMessage({ id: getIssuesTranslationKey(issueCount) }, { issueCount })}
        </span>
    );
};

// eslint-disable-next-line react/display-name
export const getFormatCriticalIssuesColumn = (formatMessage, cell) => {
    const issueCount = Number(cell);
    const severityClass = issueCount > 0 ? 'text-color-danger' : '';

    return (
        <div
            className={`display-flex align-items-center gap-5 white-space-nowrap${severityClass ? ` ${severityClass}` : ''}`}
        >
            {severityClass ? <i className='uikit-icon icon-error-sign' /> : ''}
            {formatMessage({ id: getIssuesTranslationKey(issueCount) }, { issueCount })}
        </div>
    );
};

// eslint-disable-next-line no-confusing-arrow,react/display-name
export const getFormatEmptyStringField = cell => (isEmpty(cell) ? NO_DATA_TEXT : <span>{cell}</span>);

// eslint-disable-next-line react/display-name
export const getFormatVehicleName = (formatMessage, vehicleName) => {
    if (vehicleName) {
        return <span>{vehicleName}</span>;
    }

    return (
        <a
            href={getServiceLocation('vehicleAdmin')}
            target='_blank'
            onClick={event => event.stopPropagation() /* avoid triggering other onClick handlers besides href */}
            rel='noreferrer'
        >
            {formatMessage({ id: 'intl-msg:addNewVehicle' })}
        </a>
    );
};

// eslint-disable-next-line no-confusing-arrow
export const getFormatPdfField = (formatMessage, locale, pdfUrl, item) => (
    <AnalyticsStore data={{ eventCategory: 'table', pdc: item }}>
        <PDFDownloadButtonComponent formatMessage={formatMessage} locale={locale} pdfUrl={pdfUrl} />
    </AnalyticsStore>
);

// eslint-disable-next-line react/display-name
export const getFormatEmptyDateTimeField = (cell, locale = DEFAULT_LOCALE) => {
    const dateTimeCell = formatDateTime(cell, locale);
    return isEmpty(dateTimeCell) ? NO_DATA_TEXT : <span>{dateTimeCell}</span>;
};

// Text of a cell as it is shown in the table, used for the CSV export
export const getCsvCellText = (formatMessage, locale, columnKey, item) => {
    switch (columnKey) {
        case 'criticalIssueCount':
        case 'issueCount': {
            const issueCount = Number(item[columnKey]);
            return formatMessage({ id: getIssuesTranslationKey(issueCount) }, { issueCount });
        }
        case 'vehicleNameInfo':
            return item.vehicleNameInfo || formatMessage({ id: 'intl-msg:addNewVehicle' });
        case 'modificationTimestamp':
            return formatDateTime(item.modificationTimestamp, locale) || NO_DATA_CHAR;
        default:
            return isEmpty(item[columnKey]) ? NO_DATA_CHAR : item[columnKey];
    }
};

const downloadCSV = (columns, rows, getCellText) => {
    downloadFile(
        btoa(unescape(encodeURIComponent(tableToCSV(columns, rows, getCellText)))),
        'text/csv',
        `${new Date().toUTCString()}.csv`
    );
};

export const handleTableSelectionChange = (selectedElements, selectPdc, setSelectedElements, showSidebar) => {
    const [inspectionId] = selectedElements;
    showSidebar(!!(inspectionId && true));
    selectPdc(inspectionId);
    setSelectedElements(selectedElements);
};

const getSortedCopyByAttribute = (originalArray, attribute, sortOrder) => {
    // Create a shallow copy of the original array using the spread operator
    const sortedArray = [...originalArray];
    if (!originalArray || !attribute) {
        return sortedArray;
    }
    // Sort the copy based on the specified attribute
    sortedArray.sort((a, b) => {
        const attrFromA = a[attribute] ?? '';
        const attrFromB = b[attribute] ?? '';
        const comparison =
            typeof attrFromA === 'number' && typeof attrFromB === 'number'
                ? attrFromA - attrFromB
                : String(attrFromA).localeCompare(String(attrFromB), undefined, {
                      numeric: true,
                      sensitivity: 'base',
                  });

        return sortOrder === SortOrder.ASC ? comparison : -comparison;
    });
    return sortedArray;
};

// eslint-disable-next-line max-statements, max-lines-per-function
const PDCPageComponentWithoutInjection = ({
    intl: { formatMessage, locale },
    pdc,
    filters,
    loadMore,
    selectPdc,
    pdcFetchMoreInspections,
    downloadProductActivations,
    showSidebar,
}) => {
    const [sortByField, setSortByField] = useState(undefined);
    const [sortOrder, setSortOrder] = useState(SortOrder.DESC);
    const [selectedElements, setSelectedElements] = useState([]);
    const [storedColumns, setStoredColumns] = useLocalStorage(PDC_TABLE_COLUMNS_STORAGE_KEY, []);
    const [tableResetKey, setTableResetKey] = useState(0);
    const [viewType, setViewType] = useState('TABLE');
    const [gridLines, setGridLines] = useState(false);
    const [compactRows, setCompactRows] = useState(false);
    useEffect(() => {
        downloadProductActivations();
    }, []);

    const columns = normalizeTableColumns(storedColumns, formatMessage);
    const selectedElement = (selectedElements || []).length === 1 ? selectedElements[0] : undefined;
    const handleActiveRowChange = rowId => {
        handleTableSelectionChange([rowId], selectPdc, setSelectedElements, showSidebar);
    };
    const handleSortChange = (fieldName, sortDirection) => {
        // like before the migration: a newly sorted column starts descending, clicking it again toggles
        setSortOrder(fieldName === sortByField ? sortDirection : SortOrder.DESC);
        setSortByField(fieldName);
    };
    const handleColumnResize = (columnKey, width) => {
        setStoredColumns(previousColumns =>
            normalizeTableColumns(previousColumns, formatMessage).map(column => {
                if (column.key !== columnKey) {
                    return column;
                }

                if (width === undefined) {
                    const { width: _width, ...columnWithoutWidth } = column;
                    return columnWithoutWidth;
                }

                return { ...column, width };
            })
        );
    };
    const handleColumnOrderChange = columnOrder => {
        setStoredColumns(previousColumns => {
            const normalizedColumns = normalizeTableColumns(previousColumns, formatMessage);
            const columnsByKey = new Map(normalizedColumns.map(column => [column.key, column]));
            const orderedColumns = columnOrder.flatMap(columnKey => {
                const column = columnsByKey.get(columnKey);
                return column ? [column] : [];
            });
            return [...orderedColumns, ...normalizedColumns.filter(column => !columnOrder.includes(column.key))];
        });
    };
    const handleColumnVisibilityChange = (columnKey, visible) => {
        setStoredColumns(previousColumns =>
            normalizeTableColumns(previousColumns, formatMessage).map(column =>
                column.key === columnKey ? { ...column, visible } : column
            )
        );
    };
    const handleColumnReset = () => {
        window.localStorage.removeItem(PDC_TABLE_COLUMNS_STORAGE_KEY);
        setStoredColumns([]);
        setTableResetKey(previousValue => previousValue + 1);
    };

    const sortedItems = getSortedCopyByAttribute(pdc.items, sortByField, sortOrder);
    const visibleColumns = columns.filter(column => column.visible !== false);

    return (
        <div>
            <PDCOverviewTiles />
            <TableFilterBar
                downloadTableAsCSV={() => {
                    downloadCSV(
                        visibleColumns.filter(column => column.key !== 'pdfLink'),
                        sortedItems,
                        (columnKey, item) => getCsvCellText(formatMessage, locale, columnKey, item)
                    );
                }}
                filters={filters}
                columns={columns}
                gridLines={gridLines}
                compactRows={compactRows}
                onColumnOrderChange={handleColumnOrderChange}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                onColumnReset={handleColumnReset}
                onToggleGridLines={() => setGridLines(previousValue => !previousValue)}
                onToggleCompactRows={() => setCompactRows(previousValue => !previousValue)}
                onViewTypeChange={setViewType}
                viewType={viewType}
            />
            <Table
                key={`pdc-table-${tableResetKey}`}
                id='PDCTable'
                rowKey='id'
                viewType={viewType}
                hover
                gridLines={gridLines}
                condensed={compactRows}
                activeRowId={selectedElement}
                onActiveRowChange={handleActiveRowChange}
                sortBy={sortByField}
                sortDirection={sortOrder}
                onSortChange={handleSortChange}
                onColumnResize={handleColumnResize}
                columnOrder={columns.map(column => column.key)}
                onColumnOrderChange={handleColumnOrderChange}
                responsiveBreakpoint={{ list: '800px' }}
                onViewTypeChange={setViewType}
            >
                <TableHeader>
                    {visibleColumns.map(column => (
                        <TableHeaderColumn
                            key={column.key}
                            columnKey={column.key}
                            width={column.width}
                            sortable={column.sortable}
                            draggable={column.draggable !== false}
                            resizeable={column.resizeable !== false}
                            hideLabel={column.hideLabel}
                            label={column.label}
                            iconName={column.icon}
                        >
                            {column.label}
                        </TableHeaderColumn>
                    ))}
                </TableHeader>
                <TableBody>
                    {sortedItems.map(item => (
                        <TableRow key={item.id} id={item.id} className={getSeverityClass(item)}>
                            {visibleColumns.map(column => {
                                switch (column.key) {
                                    case 'criticalIssueCount':
                                        return (
                                            <TableColumn key={column.key} columnKey={column.key}>
                                                {getFormatCriticalIssuesColumn(formatMessage, item.criticalIssueCount)}
                                            </TableColumn>
                                        );
                                    case 'issueCount':
                                        return (
                                            <TableColumn key={column.key} columnKey={column.key}>
                                                {getFormatIssuesColumn(formatMessage, item.issueCount)}
                                            </TableColumn>
                                        );
                                    case 'reportedBy':
                                        return (
                                            <TableColumn key={column.key} columnKey={column.key}>
                                                {getFormatEmptyStringField(item.reportedBy)}
                                            </TableColumn>
                                        );
                                    case 'vehicleNameInfo':
                                        return (
                                            <TableColumn key={column.key} columnKey={column.key}>
                                                {getFormatVehicleName(formatMessage, item.vehicleNameInfo)}
                                            </TableColumn>
                                        );
                                    case 'vin':
                                        return (
                                            <TableColumn key={column.key} columnKey={column.key}>
                                                {getFormatEmptyStringField(item.vin)}
                                            </TableColumn>
                                        );
                                    case 'trailerNameInfo':
                                        return (
                                            <TableColumn key={column.key} columnKey={column.key}>
                                                {getFormatEmptyStringField(item.trailerNameInfo)}
                                            </TableColumn>
                                        );
                                    case 'modificationTimestamp':
                                        return (
                                            <TableColumn key={column.key} columnKey={column.key}>
                                                {getFormatEmptyDateTimeField(item.modificationTimestamp, locale)}
                                            </TableColumn>
                                        );
                                    case 'pdfLink':
                                        return (
                                            <TableColumn key={column.key} columnKey={column.key} preventRowClick>
                                                {getFormatPdfField(formatMessage, locale, item.pdfLink, item)}
                                            </TableColumn>
                                        );
                                    default:
                                        return null;
                                }
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {loadMore && (
                <div id='loadMore' className='text-center'>
                    <Button className='margin-20' onClick={pdcFetchMoreInspections.bind(null, loadMore)}>
                        {formatMessage({ id: 'intl-msg:table.loadMore' })}
                    </Button>
                </div>
            )}
        </div>
    );
};

PDCPageComponentWithoutInjection.propTypes = {
    intl: PropTypes.shape({
        formatMessage: PropTypes.func.isRequired,
        locale: PropTypes.any,
    }).isRequired,
    pdc: PropTypes.shape(
        PropTypes.objectOf({
            items: PropTypes.array.isRequired,
            links: PropTypes.object.isRequired,
        })
    ).isRequired,
    filters: PropTypes.array.isRequired,
    downloadProductActivations: PropTypes.func.isRequired,
    loadMore: PropTypes.any,
    selectPdc: PropTypes.any,
    pdcFetchMoreInspections: PropTypes.any,
    showSidebar: PropTypes.any,
};

const PDCPageComponent = props => {
    const dispatch = useDispatch();
    const loadMore = useSelector(state => get(getInspections(state), 'links.next.href'));

    return (
        <PDCPageComponentWithoutInjection
            {...props}
            loadMore={loadMore}
            selectPdc={id => dispatch(pdcSelectedAction(id))}
            pdcFetchMoreInspections={url => dispatch(pdcFetchMoreInspectionsAction(url))}
            downloadProductActivations={() => dispatch(downloadProductActivationsAction())}
            showSidebar={display => dispatch(showPdcSidebarAction(display))}
        />
    );
};

export { PDCPageComponentWithoutInjection };
export default injectIntl(PDCPageComponent);
